import { useEffect, useMemo, useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { htmlToMarkdown, markdownToHtml } from "@/lib/brand-site-html-markdown";
import { rewriteC456AssetPathsInHtml } from "@/lib/hermes-html-preview";
import { injectPreviewBridge } from "@/lib/brand-site-preview-nav";
import { cn } from "@/lib/utils";

export type ItemHtmlEditorMode = "markdown" | "html" | "preview";

type Props = {
  id: string;
  label: string;
  value: string;
  onChange: (html: string) => void;
  assetBaseUrl?: string | null;
};

const MODES: { id: ItemHtmlEditorMode; label: string }[] = [
  { id: "markdown", label: "Markdown" },
  { id: "html", label: "HTML" },
  { id: "preview", label: "预览" },
];

function wrapBodyPreviewHtml(bodyHtml: string): string {
  const inner = bodyHtml.trim() || "<p class=\"empty\">（正文为空）</p>";
  return `<!DOCTYPE html><html lang="zh-CN"><head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>
body{margin:0;padding:1.25rem 1.5rem;font:16px/1.7 system-ui,-apple-system,sans-serif;color:#1a1a1a;background:#fff}
h1,h2,h3{line-height:1.3;margin:1.25em 0 .5em}p,ul,ol,blockquote{margin:.75em 0}
img{max-width:100%;height:auto}a{color:#2563eb}.empty{color:#888}
</style></head><body>${inner}</body></html>`;
}

/** 栏目条目 HTML 字段：Markdown / HTML / 预览三模（预览只读，无点选改稿） */
export function BrandSiteItemHtmlField({
  id,
  label,
  value,
  onChange,
  assetBaseUrl,
}: Props) {
  const [mode, setMode] = useState<ItemHtmlEditorMode>("preview");
  const [mdDraft, setMdDraft] = useState(() => htmlToMarkdown(value));
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (mode === "markdown") {
      setMdDraft(htmlToMarkdown(value));
    }
  }, [mode, value]);

  const srcDoc = useMemo(() => {
    const raw = wrapBodyPreviewHtml(value);
    return injectPreviewBridge(rewriteC456AssetPathsInHtml(raw, assetBaseUrl ?? null), {
      pick: false,
    });
  }, [assetBaseUrl, value]);

  useEffect(() => {
    if (mode !== "preview") return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    iframe.srcdoc = srcDoc;
  }, [mode, srcDoc]);

  function switchMode(next: ItemHtmlEditorMode) {
    if (mode === "markdown" && next !== "markdown") {
      onChange(markdownToHtml(mdDraft));
    }
    setMode(next);
  }

  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label htmlFor={mode === "preview" ? undefined : id}>{label}</Label>
        <div
          className="inline-flex rounded-lg border border-border bg-muted/40 p-0.5"
          role="tablist"
          aria-label="正文编辑模式"
        >
          {MODES.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={mode === item.id}
              className={cn(
                "rounded-md px-2.5 py-1 text-xs transition-colors",
                mode === item.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => switchMode(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {mode === "markdown" ? (
        <Textarea
          id={id}
          value={mdDraft}
          onChange={(event) => {
            const next = event.target.value;
            setMdDraft(next);
            onChange(markdownToHtml(next));
          }}
          rows={12}
          className="min-h-[220px] font-mono text-sm"
          placeholder="用 Markdown 写正文…"
        />
      ) : null}

      {mode === "html" ? (
        <Textarea
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={12}
          className="min-h-[220px] font-mono text-sm"
        />
      ) : null}

      {mode === "preview" ? (
        <div className="overflow-hidden rounded-2xl border border-border bg-neutral-200/60">
          <iframe
            ref={iframeRef}
            title="正文预览"
            sandbox="allow-scripts"
            className="h-[min(60vh,480px)] w-full bg-white"
          />
        </div>
      ) : null}
    </div>
  );
}
